import { useEffect, useState } from "react";
import { api, media } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Check, Flame, RefreshCw, ChevronRight, Zap } from "lucide-react";
import { Ring, CoachBadge, Disclaimer } from "@/components/program/shared";

const SITUATIONS = [
  ["only_20_min", "Only 20 minutes"], ["traveling", "Traveling"], ["eating_out", "Eating out"],
  ["no_gym", "No gym today"], ["poor_sleep", "Poor sleep"], ["very_busy", "Very busy"],
  ["missed_prep", "Missed meal prep"], ["sore", "Sore / recovering"],
];

const TaskRow = ({ t, onToggle, onSwap }) => {
  const [open, setOpen] = useState(false);
  const d = t.detail || {};
  return (
    <div className="glass rounded-xl p-4" data-testid={`task-${t.id}`}>
      <div className="flex items-center gap-3">
        <button onClick={() => onToggle(t)} data-testid={`task-toggle-${t.id}`}
          className={`w-7 h-7 rounded-lg border flex items-center justify-center shrink-0 transition-all ${t.done ? "bg-cyan-400 border-cyan-400 scale-105" : "border-white/20 hover:border-cyan-400/50"}`}>
          {t.done && <Check size={16} className="text-black" />}
        </button>
        <button onClick={() => setOpen((o) => !o)} className="flex-1 text-left min-w-0">
          <div className={`text-sm ${t.done ? "text-slate-500 line-through" : "text-white"}`}>{t.title}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">{t.type}</div>
        </button>
        {t.type === "nutrition" && <span className="text-[11px] font-mono text-cyan-300 shrink-0">{d.calories} kcal</span>}
        <ChevronRight size={15} className={`text-slate-500 transition-transform ${open ? "rotate-90" : ""}`} onClick={() => setOpen((o) => !o)} />
      </div>
      {open && (
        <div className="mt-3 pl-10 text-[13px] text-slate-300 space-y-2" data-testid={`task-detail-${t.id}`}>
          {t.type === "nutrition" && (
            <>
              <div className="flex gap-4 font-mono text-[11px] text-slate-400">
                <span>P {d.protein}g</span><span>C {d.carbs}g</span><span>F {d.fat}g</span>{d.prep_time && <span>{d.prep_time} min</span>}
              </div>
              {d.ingredients?.length > 0 && <div className="text-[12px]">Ingredients: {d.ingredients.join(", ")}</div>}
              {d.instructions && <div className="text-[12px] text-slate-400">{d.instructions}</div>}
              <Button size="sm" variant="outline" onClick={() => onSwap(t)} data-testid={`meal-swap-${t.id}`}><RefreshCw size={12} className="mr-1.5" /> Swap meal</Button>
            </>
          )}
          {t.type === "training" && d.exercises && (
            <ul className="space-y-1 font-mono text-[12px] text-slate-400">
              {d.exercises.map((e, i) => <li key={i}>{e.name} — {e.sets}×{e.reps}</li>)}
            </ul>
          )}
          {t.type !== "nutrition" && t.type !== "training" && d.note && <div>{d.note}</div>}
        </div>
      )}
    </div>
  );
};

export const Today = ({ accessId, setView }) => {
  const [data, setData] = useState(null);
  const [adaptOpen, setAdaptOpen] = useState(false);
  const [swap, setSwap] = useState(null);

  const load = () => api.get(`/programs/${accessId}/today`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accessId]);

  if (!data) return <div className="pt-10 font-mono text-xs text-cyan-300 animate-pulse">Loading…</div>;
  if (!data.ready) return <div className="pt-10 text-slate-400">Your program is still being prepared.</div>;

  const day = data.day;
  const toggle = async (t) => {
    const { data: r } = await api.post(`/programs/${accessId}/task/toggle`, { week_number: data.week_number, day_index: day.day_index, task_id: t.id, done: !t.done });
    setData((d) => ({ ...d, day: { ...d.day, tasks: d.day.tasks.map((x) => x.id === t.id ? { ...x, done: !x.done } : x) }, progress: r.progress }));
    if (!t.done && r.progress.pct === 100) toast.success("Day complete — beautiful work.");
  };
  const doSwap = async (reason) => {
    try {
      const { data: r } = await api.post(`/programs/${accessId}/meal/swap`, { week_number: data.week_number, day_index: day.day_index, task_id: swap.id, reason });
      toast.success(`Swapped to ${r.meal.name}`); setSwap(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No compliant swap available"); setSwap(null); }
  };
  const adapt = async (situation) => {
    try { const { data: r } = await api.post(`/programs/${accessId}/quick-adapt`, { week_number: data.week_number, day_index: day.day_index, situation }); toast.success(r.message); setAdaptOpen(false); load(); }
    catch { toast.error("Could not adapt"); }
  };

  return (
    <div className="pt-6" data-testid="program-today">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono-label">{data.phase_name} · Week {data.week_number}</div>
          <h1 className="font-serif text-4xl text-white mt-1">{day.day_name}</h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-sm"><Flame size={16} className="text-amber-400" /><span className="font-mono text-white">{data.streaks.current}</span><span className="text-[10px] text-slate-500 font-mono uppercase">streak</span></div>
          <Ring pct={data.progress.pct} label={`${data.progress.done}/${data.progress.total}`} />
        </div>
      </div>

      <div className="mt-4"><CoachBadge coach={data.coach} /></div>

      <Button onClick={() => setAdaptOpen(true)} variant="outline" className="mt-5 border-cyan-400/30 text-cyan-300" data-testid="my-day-changed">
        <Zap size={14} className="mr-2" /> My day changed
      </Button>

      <div className="mt-5 space-y-3">
        {day.tasks.map((t) => <TaskRow key={t.id} t={t} onToggle={toggle} onSwap={setSwap} />)}
      </div>

      {day.tip && (
        <div className="glass rounded-xl p-4 mt-4" data-testid="tip-of-day">
          <div className="mono-label mb-1">Tip of the day</div>
          <div className="text-sm text-slate-300">{day.tip}</div>
        </div>
      )}

      <Disclaimer className="mt-6" />

      <Sheet open={adaptOpen} onOpenChange={setAdaptOpen}>
        <SheetContent side="bottom" className="glass-strong border-white/10" data-testid="quick-adapt-sheet">
          <SheetHeader><SheetTitle className="font-serif text-2xl text-white">Life happens. Let&apos;s adjust today.</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 pb-4">
            {SITUATIONS.map(([k, l]) => (
              <button key={k} onClick={() => adapt(k)} data-testid={`adapt-${k}`}
                className="glass rounded-xl p-3 text-xs text-slate-200 hover:border-cyan-400/40 border border-transparent">{l}</button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={!!swap} onOpenChange={(o) => !o && setSwap(null)}>
        <SheetContent side="bottom" className="glass-strong border-white/10" data-testid="swap-reason-sheet">
          <SheetHeader><SheetTitle className="font-serif text-2xl text-white">Why swap this meal?</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4 pb-4">
            {["Don't like it", "Don't have ingredients", "Eating out", "Too expensive", "Too much cooking", "Allergy/intolerance"].map((r) => (
              <button key={r} onClick={() => doSwap(r)} data-testid={`swap-reason-${r.slice(0, 6)}`}
                className="glass rounded-xl p-3 text-xs text-slate-200 hover:border-cyan-400/40 border border-transparent">{r}</button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
